import type { MapStateToPropsFactory } from 'react-redux';
import type { LimitOrderInputFormSelectors } from 'state/secondaryMarket/limit-order/form/input/inputForm.selectors';
import type { LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import type { LimitOrderThunks } from 'state/secondaryMarket/limit-order/limitOrder.thunk';
import type { LiveInputFormSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import type { LiveSelectors } from 'state/secondaryMarket/live/live.selector';
import type { LiveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { ReferentialSelector } from 'state/secondaryMarket/shared/referential/referential.selector';
import { fromSecondaryPostEventsWorkspace } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import {
  getSecondaryMarketQuoteErrorKind,
  getSecondaryMarketQuoteErrorMessage,
} from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { getTabIsFrozen, getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';
import type { HttpErrorKind } from 'utils/errors/errors';

type OwnProps = Readonly<{
  tabId: string;
  workspaceType: SecondaryWorkspaceType;
}>;

type StateProps = Readonly<{
  bcsProductKey: BcsProductKey | undefined;
  isLoading: boolean;
  productName: string | undefined;
  traderLabel: string | undefined;
  hasFatalError: boolean;
  canResetForm: boolean;
  errors: SecondaryMarketFormError[];
  modifiedFields: string[];
  errorKind?: HttpErrorKind;
  errorMessage?: string;
}>;

type DispatchProps = Readonly<{
  onResetForm: () => void;
  onRetryProductFetch?: () => void;
}>;
export const formConnectFactory = (
  referentialSelectors: ReferentialSelector,
  secondaryMarketSelectors: LiveSelectors | LimitOrderSelectors,
  formInputSelectors: LiveInputFormSelectors | LimitOrderInputFormSelectors,
  secondaryMarketThunks: LiveThunks | LimitOrderThunks,
) => {
  const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
    return (state, { tabId }) => {
      const bcsProductKey = fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
      const dataLoadingStatus = fromSecondaryPostEventsWorkspace.getSecondaryMarketPostEventLoadingStatus(state, tabId);
      const tabContentLoadingStatus = getTabLoadingStatus(state, tabId);
      const productName = referentialSelectors.getProductName(state, tabId);
      const traderLabel = referentialSelectors.getTraderLabel(state, tabId);
      const hasFatalError =
        tabContentLoadingStatus === TabLoadingStatus.LoadingFailed || dataLoadingStatus === 'failed';
      const isLoading = tabContentLoadingStatus === TabLoadingStatus.Loading || dataLoadingStatus === 'loading';
      const isFrozen = getTabIsFrozen(state, tabId) ?? false;
      const errors = secondaryMarketSelectors.getAmendValidationErrors(state, tabId) as SecondaryMarketFormError[];
      const canResetForm = !isFrozen;
      const modifiedFields = formInputSelectors.getFormChanges(state, tabId);
      const errorKind = getSecondaryMarketQuoteErrorKind(state, tabId);
      const errorMessage = getSecondaryMarketQuoteErrorMessage(state, tabId);

      return {
        bcsProductKey,
        productName,
        traderLabel,
        isLoading,
        hasFatalError,
        canResetForm,
        errors,
        modifiedFields,
        errorKind,
        errorMessage,
      };
    };
  };
  const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
    return {
      onResetForm: () => {
        dispatch(secondaryMarketThunks.amendResetTabThunk(tabId));
      },
      onRetryProductFetch() {
        dispatch(secondaryMarketThunks.reloadTabThunk(tabId));
      },
    };
  };
  return { mapStateToProps, mapDispatchToProps };
};
